import { defineComponent as _defineComponent } from 'vue'
import {
  computed, nextTick, PropType, provide, ref, toRefs, useSlots
} from 'vue';
import IconSkills from '@/components/svg/profile-skills.svg?inline';
import EmptySection from '@/views/profile/components/EmptySection.vue';
import IconProjects from '@/components/svg/profile-projects.svg?inline';
import IconReferences from '@/components/svg/profile-references.svg?inline';
import IconPreferences from '@/components/svg/profile-preferences.svg?inline';
import useProfileState from '@/state/profile/profileState';
import useProfilePreview from '@/state/profile/profilePreview';
import useProfilePdfUpload, { ProfilePdfReanalyzeSource } from '@/views/profile/composables/useProfilePdfUpload';
import type { ReanalyzableProfileSection } from '@/views/profile/utils/buildReanalyzeUserPrompt';
import AiSuggestionReviewBar from '@/views/profile/components/profile-pdf-upload/AiSuggestionReviewBar.vue';
import IconCertificates from '@/components/svg/profile-certificates.svg?inline';
import IconAboutUs from '@/components/svg/profile-aboutus.svg?inline';
import IconMembersGuests from '@/components/svg/profile-members-guests.svg?inline';
import IconPublicProjects from '@/components/svg/profile-public-projects.svg?inline';
import EditIcon from '@/components/molecules/EditIcon.vue';
import BCXChevronSwitch from '@/components/molecules/BCXChevronSwitch.vue';
import { ProfileSectionKey } from '@/models/Profile';
import useUser from '@/mixins/useUser';
import usePageState from '@/state/pageState';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import { logError } from '@/utils/logger';
import { AxiosError } from 'axios';
import scrollIntoView from '@/utils/scrollIntoView';
import ProfileSectionBusyOverlay from '@/views/profile/components/ProfileSectionBusyOverlay.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileSection',
  props: {
  section: {
    type: String as PropType<ProfileSectionKey>,
    default: '',
  },
  loadingAnimation: {
    type: Number,
    default: 1,
  },
  loadingAnimationSize: {
    type: Number,
    default: 50,
  },
},
  setup(__props) {

const props = __props;

const icons = {
  skills: IconSkills,
  projectPreferences: IconPreferences,
  references: IconReferences,
  certificatesEducations: IconCertificates,
  projects: IconProjects,
  aboutus: IconAboutUs,
  members: IconMembersGuests,
  publicprojects: IconPublicProjects
} as any;

const expandableSections = [];



const slots = useSlots();
const headlineElement = ref<HTMLElement | null>(null);
const isBusy = ref(false);
const isAcceptingPreview = ref(false);

const { section } = toRefs(props);
const isExpanded = ref(true);

provide('currentEditSection', section);
provide('profileSectionBusy', isBusy);

const {
  isSelf, editSections, toggleEditSection, isSectionFilled, isQuickInfo, user, acceptSection, savedProfile
} = useProfileState();

const { hasUnresolvedFor, discardSection, previewProfile } = useProfilePreview();
const {
  isReanalyzing,
  activeReanalyzeSection,
  hasReanalyzeSource,
  reanalyzeSection,
  cancelReanalyze,
} = useProfilePdfUpload();

const { tUserFallback } = useUser(user);

const edit = () => {
  toggleEditSection(section.value);
};

const icon = computed(() => icons[section.value]);

const headline = computed(() => (section.value ? tUserFallback(`profile.${section.value}.headline`) : ''));
const emptyText = computed(() => tUserFallback(`profile.${section.value}.emptyText`));

const hasEditorContent = slots?.editor;

const isBeingEdited = computed(
  () => !isQuickInfo && editSections.value.includes(section.value),
);

const hasPreview = computed(() => hasUnresolvedFor(section.value));
const showAiSuggestionReviewBar = computed(() => hasPreview.value && !isBeingEdited.value);
const isActiveReanalyzeBar = computed(() => (
  isReanalyzing.value && activeReanalyzeSection.value === section.value
));

const resolveReanalyzePdfSource = (): ProfilePdfReanalyzeSource => {
  const previewExperiences = previewProfile.value.experiences;
  const savedExperiences = savedProfile.value?.experiences;

  return {
    pdfBase64: previewExperiences?.experiencesDocument ?? savedExperiences?.experiencesDocument ?? '',
    pdfFileName: previewExperiences?.experiencesDocumentLabel ?? savedExperiences?.experiencesDocumentLabel ?? '',
  };
};
const hasReanalyzeFallbackSource = computed(() => {
  const { pdfBase64, pdfFileName } = resolveReanalyzePdfSource();
  return !!pdfBase64 && !!pdfFileName;
});
const disableNewSuggestion = computed(() => {
  if (isActiveReanalyzeBar.value) return false;
  return (
    (isReanalyzing.value && activeReanalyzeSection.value !== section.value)
    || (!hasReanalyzeSource.value && !hasReanalyzeFallbackSource.value)
  );
});

const REANALYZE_SECTIONS: Partial<Record<ProfileSectionKey, ReanalyzableProfileSection>> = {
  skills: 'skills',
  educations: 'educations',
  certificates: 'certificates',
  projects: 'projects',
};

const { globalErrorMessage } = usePageState();
const busyOverlayBottomOffset = computed(() => (
  section.value === 'projects' && isAcceptingPreview.value ? 300 : null
));
const onAcceptPreview = async () => {
  isBusy.value = true;
  isAcceptingPreview.value = true;

  try {
    await acceptSection(section.value);
  } catch (e) {
    const i18Key = (e as Error & { savedButReloadFailed?: boolean }).savedButReloadFailed
      ? 'profile.common.reloadFailedAfterSave'
      : 'profilePdfUpload.errors.acceptFailed';
    globalErrorMessage.value = {
      i18Key,
      code: getAxiosResponseErrorCode(e as AxiosError),
      component: 'ProfileSection.vue',
      withoutReload: true,
      timeoutMs: 15000,
    };
    logError('Failed to accept profile preview', section.value, e);
  } finally {
    isAcceptingPreview.value = false;
    isBusy.value = false;
  }
};
const onEditPreview = async () => {
  // Slot stays populated; the bar hides because the section enters edit mode.
  // The editor reads from effectiveProfile so it sees the preview content pre-filled.
  toggleEditSection(section.value);

  if (section.value !== 'projects') return;

  await nextTick();

  if (headlineElement.value) {
    scrollIntoView(headlineElement.value);
  }
};
const onDiscardPreview = async () => {
  discardSection(section.value);

  if (section.value !== 'projects') return;

  await nextTick();

  if (headlineElement.value) {
    scrollIntoView(headlineElement.value);
  }
};
const onNewSuggestion = async () => {
  const reanalyzeTargetSection = REANALYZE_SECTIONS[section.value];
  if (!reanalyzeTargetSection) return;

  await reanalyzeSection(reanalyzeTargetSection, resolveReanalyzePdfSource());
};
const hasData = computed(
  () => isSectionFilled.value[section.value],
);
const showEmptySection = computed(
  () => !hasData.value && section.value && !isBeingEdited.value,
);
const showEditButton = computed(
  () => hasEditorContent && isSelf.value && headline.value && !isBeingEdited.value && section.value && !showAiSuggestionReviewBar.value,
);
const showSection = computed(
  () => hasData.value || (isSelf.value && !isQuickInfo) || !section.value,
);

const hasIconSlot = computed(() => !!slots.icon);

const hasHeadlineSlot = computed(() => !!slots.headline);

const isExpandable = computed(() => expandableSections.includes(section.value));

return { __sfc: true,icons, expandableSections, props, slots, headlineElement, isBusy, isAcceptingPreview, section, isExpanded, isSelf, editSections, toggleEditSection, isSectionFilled, isQuickInfo, user, acceptSection, savedProfile, hasUnresolvedFor, discardSection, previewProfile, isReanalyzing, activeReanalyzeSection, hasReanalyzeSource, reanalyzeSection, cancelReanalyze, tUserFallback, edit, icon, headline, emptyText, hasEditorContent, isBeingEdited, hasPreview, showAiSuggestionReviewBar, isActiveReanalyzeBar, resolveReanalyzePdfSource, hasReanalyzeFallbackSource, disableNewSuggestion, REANALYZE_SECTIONS, globalErrorMessage, busyOverlayBottomOffset, onAcceptPreview, onEditPreview, onDiscardPreview, onNewSuggestion, hasData, showEmptySection, showEditButton, showSection, hasIconSlot, hasHeadlineSlot, isExpandable, EmptySection, AiSuggestionReviewBar, EditIcon, BCXChevronSwitch, ProfileSectionBusyOverlay }
}

})